import { captureException } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { endServerSession } from '../../api/logout';
import { LOGIN_SESSION_END } from '../../modules/analytics/events';
import { trackEvent, trackReset } from '../../modules/analytics/trackingMiddleware';
import { forgetThisBrowser, resetInAppSession } from '../../modules/close-the-loop';
import { PASSCODE_ROUTES } from '../../navigation/paths';
import localStorageStore from '../../store/localStorageStore';
import { useUnifiedDashboardStore } from '../../store/UnifiedDashboardStore';

export interface LogoutOptions {
  readonly navigateTo?: string | null;
}

export const useLogout = ({ navigateTo = PASSCODE_ROUTES.login }: LogoutOptions = {}): (() => void) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return () => {
    trackEvent(LOGIN_SESSION_END, { url: window.location.href });
    trackReset();
    forgetThisBrowser()
      .then(endServerSession)
      .catch((error: unknown) => {
        captureException(error, { tags: { feature: 'logout' } });
        toast.warn(
          t(
            'logout.serverUnconfirmed',
            'You’re signed out on this device, but the server didn’t confirm it. On a shared computer, close the browser.',
          ),
        );
      });
    queryClient.clear();
    resetInAppSession();
    localStorageStore.logout();
    useUnifiedDashboardStore.getState().resetUnifiedDashboardStore();
    if (navigateTo !== null) navigate(navigateTo, { replace: true });
  };
};
